import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useMutation, useApolloClient } from '@apollo/client';
import { Checkout } from '@eurostar/ui-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import '@adyen/adyen-web/dist/adyen.css';

import { PaymentStatus, Location } from 'types/payments';
import config from 'utils/config';
import { localeToLanguage } from 'utils/locale';
import { historyShape, legShape, passengersShape } from 'constants/propTypes';
import { Spinner } from 'admin-components/components';
import { MAKE_CARD_PAYMENT, UPDATE_CUSTOMER } from 'store/mutations';
import { GET_MEMBERSHIP_DETAILS } from 'store/queries';
import usePayIfLoyaltyAndConfirmBookingMutation from 'components/mutations/usePayIfLoyaltyAndConfirmBookingMutation';
import AgentPortalError from 'errors/AgentPortalError';
import { getBookingJourneysURL, getClubEurostarSubscriptionURL } from 'utils/urls';
import { buildCustomerFromBillingDetails } from 'utils/booking';

import { Popup, PopupWrapper, PaymentContainer, PaymentLoadingOverlay } from './CardPaymentPopup.styles';

const ADYEN_CLIENT_KEY = config('ADYEN_CLIENT_KEY');
const ADYEN_ENVIRONMENT = config('ADYEN_ENVIRONMENT');

const CardPaymentPopup = ({
  isOpen,
  onClose,
  paymentAmount,
  reference: bookingReference,
  currency,
  totalPriceToBePaid,
  billingDetails,
  isSplitPayment,
  isPackage,
  hasHotel,
  history,
  isPutOnHoldWithDeposit = false,
  intl: { locale },
  outbound,
  inbound,
  passengers,
  isGroup
}) => {
  const popupWrapper = useRef();
  const client = useApolloClient();
  const [makeCardPayment, { loading: makePaymentLoading, error: makeCardPaymentError }] = useMutation(MAKE_CARD_PAYMENT, {
    refetchQueries: ['booking']
  });
  const [updateCustomer, { loading: updateCustomerLoading, error: updateCustomerError }] = useMutation(UPDATE_CUSTOMER);
  const [confirmBooking, { loading: confirmBookingLoading, error: confirmBookingError }] = usePayIfLoyaltyAndConfirmBookingMutation();

  const membershipNumber = new URLSearchParams(history.location.search).get('membershipNumber');
  const isMissedDeparture = new URLSearchParams(history.location.search).get('isMissedDeparture') === 'true';
  const isFlowForwardCapped = new URLSearchParams(history.location.search).get('isFlowForwardCapped') === 'true';

  const isSubscription = new URLSearchParams(history.location.search).get('isSubscription') === 'true';

  const subscriptionUrl = membershipNumber && isSubscription ? getClubEurostarSubscriptionURL(membershipNumber, bookingReference) : null;
  const redirectUrl = subscriptionUrl || getBookingJourneysURL(bookingReference, membershipNumber);

  const handleClick = (event) => {
    if (event.target === popupWrapper.current) {
      event.preventDefault();
      onClose();
    }
  };

  const shouldPutOnHold = isPutOnHoldWithDeposit && paymentAmount === totalPriceToBePaid;

  const getPaymentMethods = async (payload) => {
    try {
      const { data } = await axios({
        url: `${config('PAYMENT_SERVICE_URL')}/payment-methods`,
        method: 'GET',
        params: payload
      });

      return { data: data.pspData };
    } catch (error) {
      return { error };
    }
  };

  const paymentResult = async ({ resultCode }) => {
    if (resultCode !== PaymentStatus.AUTHORISED) {
      throw new Error(`Payment failed, status: ${resultCode}`);
    }

    const isTotalPrice = paymentAmount === totalPriceToBePaid;
    const isConfirmationRequired = !isPutOnHoldWithDeposit && isTotalPrice && isSplitPayment;
    const isRedirectRequired = isTotalPrice || !isSplitPayment || shouldPutOnHold;
    if (isConfirmationRequired) {
      await confirmBooking({
        variables: {
          bookingData: {
            reference: bookingReference,
            currency,
            passengers,
            outbound,
            inbound
          },
          membershipNumber,
          isMissedDeparture,
          isFlowForwardCapped,
          sendConfirmationEmail: !isGroup
        }
      });
    }

    if (isRedirectRequired) {
      history.push(redirectUrl, membershipNumber && isSubscription ? { shouldRetry: true } : undefined);
    }

    onClose();
  };

  const submitCardPayment = async (pspData) => {
    const { email, countryCode, city, postalCode, stateOrProvince, addressLine1, addressLine2 } = billingDetails;

    try {
      const res = await makeCardPayment({
        variables: {
          bookingReference,
          amount: paymentAmount,
          currency,
          isSplitPayment,
          isMissedDeparture,
          isFlowForwardCapped,
          isDepositPayment: shouldPutOnHold,
          paymentData: {
            ...pspData,
            shopperEmail: email,
            billingAddress: {
              city,
              country: countryCode,
              houseNumberOrName: addressLine1,
              postalCode,
              street: addressLine2 || addressLine1,
              stateOrProvince
            }
          }
        }
      });

      await paymentResult(res.data.makeCardPayment.pspData);

      return { data: res.data.makeCardPayment.pspData };
    } catch (error) {
      return { error };
    }
  };

  const processPayment = async (pspData) => {
    if (!isSubscription) {
      let customerInfo = null;
      if (membershipNumber) {
        try {
          const result = await client.query({
            query: GET_MEMBERSHIP_DETAILS,
            variables: {
              membershipNumber
            }
          });
          customerInfo = result?.data?.getCustomerDetails;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Cannot get customer info', error);
        }
      }

      const customer = customerInfo
        ? {
            firstName: customerInfo?.firstName || '',
            lastName: customerInfo?.lastName || '',
            email: customerInfo?.emailAddress || '',
            phoneNumber: customerInfo?.phoneNumber || '',
            address: {
              street: customerInfo?.address?.street || '',
              postalCode: customerInfo?.address?.postalCode || '',
              city: customerInfo?.address?.city || '',
              countryCode: customerInfo?.address?.countryCode || ''
            }
          }
        : buildCustomerFromBillingDetails(billingDetails);

      await updateCustomer({
        variables: {
          bookingReference,
          customer
        }
      });
    }

    return submitCardPayment(pspData);
  };

  const error = updateCustomerError || confirmBookingError || makeCardPaymentError;
  if (error) {
    throw new AgentPortalError(error);
  }

  const isLoading = makePaymentLoading || updateCustomerLoading || confirmBookingLoading;

  return (
    <PopupWrapper ref={popupWrapper} isOpen={isOpen} onClick={handleClick}>
      <Popup id="card-payment-popup" isOpen={isOpen}>
        {isOpen ? (
          <PaymentContainer>
            <Checkout
              getPaymentMethods={getPaymentMethods}
              initiatePayment={processPayment}
              location={Location.EBSC}
              hasHotel={isPackage || hasHotel}
              language={localeToLanguage(locale)}
              clientKey={ADYEN_CLIENT_KEY}
              amount={paymentAmount}
              currency={currency}
              environment={ADYEN_ENVIRONMENT}
              showPayButton
            />
            {isLoading && (
              <PaymentLoadingOverlay>
                <Spinner />
              </PaymentLoadingOverlay>
            )}
          </PaymentContainer>
        ) : null}
      </Popup>
    </PopupWrapper>
  );
};

CardPaymentPopup.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  paymentAmount: PropTypes.number.isRequired,
  reference: PropTypes.string.isRequired,
  currency: PropTypes.string.isRequired,
  totalPriceToBePaid: PropTypes.number.isRequired,
  billingDetails: PropTypes.shape({
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    addressLine3: PropTypes.string,
    city: PropTypes.string,
    countryCode: PropTypes.string,
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    phoneCountryCode: PropTypes.string,
    phoneNumber: PropTypes.string,
    postalCode: PropTypes.string,
    stateOrProvince: PropTypes.string
  }),
  isPutOnHoldWithDeposit: PropTypes.bool,
  isSplitPayment: PropTypes.bool,
  isPackage: PropTypes.bool,
  hasHotel: PropTypes.bool,
  history: historyShape,
  intl: PropTypes.shape({
    locale: PropTypes.string.isRequired
  }).isRequired,
  outbound: PropTypes.arrayOf(legShape),
  inbound: PropTypes.arrayOf(legShape),
  passengers: passengersShape,
  isGroup: PropTypes.bool
};

export default CardPaymentPopup;
